import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { AppProvider } from '@toolpad/core/AppProvider';
import { CustomContainer } from '@components/Styles/CommonStyle';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import Header from './Header.js';
import { NAVIGATION, USER_NAVIGATION } from '@constants/navigation.js';
import React, { useEffect, useRef } from 'react';
import TabNavigation from '@components/common/Shared/TabNavigation.js';
import { adminTheme } from '@components/Styles/theme';
import { parseJwt } from '@utils/jwt.js';

const ContainerWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const customContainerRef = useRef(null);

  // 페이지 이동시 화면 위치 초기화 로직. 
  useEffect(() => {
    // console.log(`[ContainerWrapper] Pathname changed: ${pathname}`);
    
    // 사업 상세페이지 댓글 탭으로 이동하는 경우 스크롤 초기화 건너뛰기
    const hasCommentTab = location.search.includes('tab=comment') || location.hash.includes('comment');
    if (hasCommentTab) {
      return;
    }
    
    let scrollableElement = null;

    const selectorsToTry = [
      '.Toolpad_DashboardLayout-main',
      'main',
      '.MuiBox-root[role="main"]',
      '.MuiDrawer-root + .MuiBox-root',
      '.MuiContainer-root',
    ];

    for (const selector of selectorsToTry) {
      if (typeof selector === 'string') {
        scrollableElement = document.querySelector(selector);
      }
     
      if (scrollableElement && scrollableElement.scrollHeight > scrollableElement.clientHeight) {
        // console.log(`[ContainerWrapper] Found scrollable element with selector: "${selector}"`, scrollableElement);
        break; // 스크롤 가능한 첫 번째 요소를 찾으면 중단
      } else if (scrollableElement) {
        // console.log(`[ContainerWrapper] Element found with selector: "${selector}" but not scrollable.`, scrollableElement);
        scrollableElement = null; // 스크롤 가능하지 않으면 초기화
      }
    }

    // CustomContainer 자체도 스크롤 가능한지 다시 한번 확인 (참고용)
    if (customContainerRef.current && customContainerRef.current.scrollHeight > customContainerRef.current.clientHeight) {
        // console.log('[ContainerWrapper] CustomContainer IS scrollable. scrollTop:', customContainerRef.current.scrollTop);
        // customContainerRef.current.scrollTop = 0; // 이전 로직에서 이미 0으로 확인됨
    } else if (customContainerRef.current) {
        // console.log('[ContainerWrapper] CustomContainer is NOT scrollable or already at top. scrollTop:', customContainerRef.current.scrollTop);
    }

    if (scrollableElement) {
      // console.log('[ContainerWrapper] Targeting element for scroll:', scrollableElement, 'Current scrollTop:', scrollableElement.scrollTop);
      scrollableElement.scrollTop = 0;
      setTimeout(() => {
        // console.log('[ContainerWrapper] Target element scrollTop after (delayed):', scrollableElement.scrollTop);
      }, 0);
    } else {
      // console.warn('[ContainerWrapper] No specific scrollable element found after trying selectors. Falling back to window.scrollTo(0,0).');
      window.scrollTo(0, 0);
      setTimeout(() => {
        // console.log('[ContainerWrapper] Fallback ScrollY after scrollTo (delayed):', window.scrollY);
      }, 0);
    }
  }, [pathname]);

  const segments = pathname.split('/');
  const firstSegment = segments[1] || '';

  const router = {
    navigate: (url) => navigate(url),
    push: (url) => navigate(url),
    replace: (url) => navigate(url, { replace: true }),
    pathname: window.location.pathname,
  };

  const token = localStorage.getItem('token');
  const parsed = token ? parseJwt(token) : null;
  const isAdmin = parsed?.isAdmin === 1 ? true : false;

  const navigation = isAdmin ? NAVIGATION : USER_NAVIGATION;

  return (
    <AppProvider navigation={navigation} router={router} theme={adminTheme}>
      <DashboardLayout
        defaultSidebarCollapsed
        slots={{ appTitle: Header }}
        navigation={navigation.filter((item) => !item.hidden)}
      >
        <CustomContainer 
          maxWidth={false} 
          ref={customContainerRef}
          style={{
            height: 'calc(100vh - 64px)',
            overflowY: 'auto',
            padding: '32px 24px'
          }}
        >
          <TabNavigation segment={firstSegment} />
          <Outlet />
        </CustomContainer>
      </DashboardLayout>
    </AppProvider>
  );
};

export default ContainerWrapper;
